(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-sh-lighting-editorial]';
  var STORAGE_KEY = 'shLightingEditorialSaved';
  var ALLOWED_MULTI_PRODUCT_GROUPS = new Set(['kiri-family', 'nova-family', 'pulsar-family']);

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (root.dataset.shInitialized === 'true') return;
      root.dataset.shInitialized = 'true';
      init(root);
    });
  }

  function init(root) {
    var dataNode = root.querySelector('[data-sh-product-data]');
    if (!dataNode) return;

    var products;
    try {
      products = JSON.parse(dataNode.textContent || '[]');
    } catch (error) {
      console.error('SickHaus lighting collection data could not be read.', error);
      return;
    }

    products = products.filter(function (product) {
      return product && product.handle;
    }).map(normalizeProduct);

    var currency = root.dataset.currency || 'GBP';
    var locale = root.dataset.locale || document.documentElement.lang || navigator.language || 'en';
    var country = root.dataset.country || '';
    if (locale.indexOf('-') === -1 && country) locale += '-' + country;
    var priceRanges = readPriceRanges();
    var collectionUrl = root.dataset.collectionUrl || window.location.pathname;
    var quickAddEnabled = root.dataset.quickAddEnabled !== 'false';
    var groupProductFamilies = root.dataset.groupProductFamilies === 'true';
    var familyGrouping = {
      'kiri-family': groupProductFamilies && root.dataset.groupKiriFamily === 'true',
      'nova-family': groupProductFamilies && root.dataset.groupNovaFamily === 'true',
      'pulsar-family': groupProductFamilies && root.dataset.groupPulsarFamily === 'true'
    };
    var grid = root.querySelector('[data-sh-product-grid]');
    var applied = root.querySelector('[data-sh-applied-filters]');
    var resultsCount = root.querySelector('[data-sh-results-count]');
    var filterCount = root.querySelector('[data-sh-filter-count]');
    var sortSelect = root.querySelector('[data-sh-sort]');
    var filterDrawer = root.querySelector('[data-sh-filter-drawer]');
    var filterBackdrop = root.querySelector('[data-sh-filter-backdrop]');
    var modal = root.querySelector('[data-sh-quick-modal]');
    var modalBackdrop = root.querySelector('[data-sh-modal-backdrop]');
    var editorialTemplate = root.querySelector('[data-sh-editorial-template]');
    var toast = root.querySelector('[data-sh-toast]');
    var activeModalProduct = null;
    var activeVariant = null;
    var lastFocus = null;
    var selectedByGroup = Object.create(null);
    var preferredVariantByProduct = Object.create(null);
    var saved = readSaved();
    var stickyFrame = null;
    var imagePromises = new Map();
    var switchSequence = 0;

    var groups = groupProducts(products);
    var state = readStateFromUrl();
    if (!products.some(function (product) { return product.type === 'Table Lamp'; })) {
      console.warn('SickHaus lighting taxonomy contains no recognised table lamps.', products.map(function (product) { return { handle: product.handle, type: product.type }; }));
    }
    if (sortSelect) sortSelect.value = state.sort;
    updateCategoryCounts();
    var liveCounter = root.querySelector('[data-sh-live-product-count]');
    if (liveCounter) liveCounter.textContent = products.length + ' live lights';

    var filterDefinitions = {
      types: [
        ['Table Lamp', 'Table lamps'],
        ['Floor Lamp', 'Floor lamps'],
        ['Night Light', 'Night lights'],
        ['Motion Light', 'Motion lights'],
        ['Projection Light', 'Projection lights'],
        ['Pendant Light', 'Pendant lights'],
        ['Wall Light', 'Wall lights']
      ],
      functions: [
        ['Ambient', 'Ambient'],
        ['Task', 'Task'],
        ['RGB / Colour-changing', 'Colour-changing'],
        ['Projection', 'Projection'],
        ['Night Light', 'Night light'],
        ['Accent', 'Accent'],
        ['Motion', 'Motion sensing']
      ],
      powers: [
        ['Mains Powered', 'Mains powered'],
        ['USB Powered', 'USB powered'],
        ['Rechargeable', 'Rechargeable'],
        ['Hardwired', 'Hardwired']
      ],
      prices: priceRanges.map(function (range) { return [range.key, range.label]; })
    };

    populateFilters();
    upgradeFilterDrawer();
    updateStickyOffset();
    syncViewButtons();
    syncFilterControls();
    render();

    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    root.addEventListener('pointerover', onPointerIntent, { passive: true });
    root.addEventListener('focusin', onPointerIntent);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('scroll', requestStickyOffset, { passive: true });
    window.addEventListener('resize', requestStickyOffset, { passive: true });


    function requestStickyOffset() {
      if (stickyFrame) return;
      stickyFrame = window.requestAnimationFrame(function () {
        stickyFrame = null;
        updateStickyOffset();
      });
    }

    function updateStickyOffset() {
      var header = document.querySelector('#SiteHeader');
      var offset = 0;
      if (header && header.classList.contains('site-header--stuck')) {
        offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      }
      root.style.setProperty('--sh-lec-sticky-top', offset + 'px');
    }

    function normalizeProduct(product) {
      product.price = Number(product.price || 0);
      product.compareAt = Number(product.compareAt || 0);
      product.manualOrder = Number(product.manualOrder || 9999);
      product.groupOrder = product.manualOrder;
      product.rating = Number(product.rating || 0);
      product.reviewCount = Number(product.reviewCount || 0);
      product.tags = arrayValue(product.tags);
      product.type = canonicalType(product.type, product.tags);
      product.functions = canonicalTerms(arrayValue(product.functions), 'function', product.tags);
      product.powers = canonicalTerms(arrayValue(product.powers), 'power', product.tags);
      product.colours = canonicalTerms(arrayValue(product.colours), 'colour', product.tags);
      product.variants = arrayValue(product.variants).map(function (variant) {
        variant.price = Number(variant.price || 0);
        variant.compareAt = Number(variant.compareAt || 0);
        variant.options = arrayValue(variant.options);
        return variant;
      });
      product.availableVariants = product.variants.filter(function (variant) { return variant.available; });
      product.available = product.availableVariants.length > 0;
      product.primaryPrice = product.availableVariants.length
        ? Math.min.apply(null, product.availableVariants.map(function (variant) { return variant.price; }))
        : product.price;
      product.classification = compactCardMeta(product);
      product.fact = '';
      product.badge = compactBadge(product.badge);
      return product;
    }

    function compactBadge(value) {
      return String(value || '').trim();
    }

    function arrayValue(value) {
      return Array.isArray(value) ? value : [];
    }

    function normalizedToken(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9-]+/g, '')
        .replace(/-+/g, '-');
    }

    function canonicalType(value, tags) {
      var token = normalizedToken(value);
      var tagTokens = arrayValue(tags).map(normalizedToken);
      var lookup = {
        'table-lamp': 'Table Lamp',
        'table-lamps': 'Table Lamp',
        'desk-lamp': 'Table Lamp',
        'desk-lamps': 'Table Lamp',
        'floor-lamp': 'Floor Lamp',
        'floor-lamps': 'Floor Lamp',
        'night-light': 'Night Light',
        'night-lights': 'Night Light',
        'motion-light': 'Motion Light',
        'motion-lights': 'Motion Light',
        'projection-light': 'Projection Light',
        'projection-lights': 'Projection Light',
        'pendant-light': 'Pendant Light',
        'pendant-lights': 'Pendant Light',
        'ceiling-light': 'Pendant Light',
        'ceiling-lights': 'Pendant Light',
        'wall-light': 'Wall Light',
        'wall-lights': 'Wall Light'
      };
      if (lookup[token]) return lookup[token];

      var tagOrder = [
        ['lightingtype-table-lamp', 'Table Lamp'],
        ['lightingtype-floor-lamp', 'Floor Lamp'],
        ['lightingtype-night-light', 'Night Light'],
        ['lightingtype-motion-light', 'Motion Light'],
        ['lightingtype-projection-light', 'Projection Light'],
        ['lightingtype-pendant-light', 'Pendant Light'],
        ['lightingtype-wall-light', 'Wall Light']
      ];
      for (var index = 0; index < tagOrder.length; index += 1) {
        if (tagTokens.indexOf(tagOrder[index][0]) !== -1) return tagOrder[index][1];
      }
      return String(value || '').trim();
    }

    function canonicalTerms(values, taxonomy, tags) {
      var result = [];
      var seen = new Set();
      var mappings = {
        function: {
          'ambient': 'Ambient',
          'task': 'Task',
          'rgb-colour-changing': 'RGB / Colour-changing',
          'rgb-color-changing': 'RGB / Colour-changing',
          'colour-changing': 'RGB / Colour-changing',
          'color-changing': 'RGB / Colour-changing',
          'projection': 'Projection',
          'night-light': 'Night Light',
          'accent': 'Accent',
          'motion': 'Motion'
        },
        power: {
          'mains-powered': 'Mains Powered',
          'mains': 'Mains Powered',
          'usb-powered': 'USB Powered',
          'usb': 'USB Powered',
          'rechargeable': 'Rechargeable',
          'hardwired': 'Hardwired',
          'hard-wired': 'Hardwired'
        },
        colour: {}
      };

      function add(value) {
        var raw = String(value || '').trim();
        if (!raw) return;
        var canonical = mappings[taxonomy][normalizedToken(raw)] || raw;
        var key = normalizedToken(canonical);
        if (seen.has(key)) return;
        seen.add(key);
        result.push(canonical);
      }

      arrayValue(values).forEach(add);
      var prefix = 'lighting' + taxonomy + '-';
      arrayValue(tags).forEach(function (tag) {
        var token = normalizedToken(tag);
        if (token.indexOf(prefix) !== 0) return;
        var valueToken = token.slice(prefix.length);
        add(mappings[taxonomy][valueToken] || valueToken.replace(/-/g, ' '));
      });
      return result;
    }

    function canGroupFamily(groupKey) {
      return ALLOWED_MULTI_PRODUCT_GROUPS.has(groupKey) && familyGrouping[groupKey] === true;
    }

    function groupProducts(list) {
      var map = new Map();
      list.forEach(function (product) {
        var requestedKey = product.group || product.handle;
        var canCombine = canGroupFamily(requestedKey);
        var key = canCombine ? requestedKey : product.handle;
        if (!map.has(key)) {
          map.set(key, {
            key: key,
            title: canCombine ? (product.groupTitle || product.title) : product.title,
            order: product.manualOrder,
            badge: product.badge || '',
            members: []
          });
        }
        var group = map.get(key);
        group.members.push(product);
        group.order = Math.min(group.order, product.manualOrder);
        if (!group.badge && product.badge) group.badge = product.badge;
      });
      return Array.from(map.values()).sort(function (a, b) { return a.order - b.order; });
    }

    function readStateFromUrl() {
      var params = new URLSearchParams(window.location.search);
      return {
        view: params.get('view') || 'all',
        types: new Set(params.getAll('type')),
        functions: new Set(params.getAll('function')),
        powers: new Set(params.getAll('power')),
        prices: new Set(params.getAll('price')),
        sort: params.get('sort') || 'featured'
      };
    }

    function writeStateToUrl() {
      var params = new URLSearchParams();
      if (state.view && state.view !== 'all') params.set('view', state.view);
      setParams(params, 'type', state.types);
      setParams(params, 'function', state.functions);
      setParams(params, 'power', state.powers);
      setParams(params, 'price', state.prices);
      if (state.sort && state.sort !== 'featured') params.set('sort', state.sort);
      var query = params.toString();
      var url = collectionUrl + (query ? '?' + query : '');
      window.history.replaceState({ shLightingEditorial: true }, '', url);
    }

    function setParams(params, key, values) {
      values.forEach(function (value) { params.append(key, value); });
    }

    function onPopState() {
      state = readStateFromUrl();
      if (sortSelect) sortSelect.value = state.sort;
      syncViewButtons();
      syncFilterControls();
      render(false);
    }

    function readPriceRanges() {
      var node = root.querySelector('[data-sh-price-range-data]');
      if (node) {
        try {
          var parsed = JSON.parse(node.textContent || '[]');
          var valid = parsed.filter(function (range) {
            return range && range.key && range.label && Number.isFinite(Number(range.min)) && (range.max === null || Number.isFinite(Number(range.max)));
          }).map(function (range) {
            return {
              key: String(range.key),
              label: String(range.label),
              min: Number(range.min),
              max: range.max === null ? null : Number(range.max)
            };
          });
          if (valid.length) return valid;
        } catch (error) {
          console.warn('SickHaus localized price filters could not be read.', error);
        }
      }
      return [
        { key: 'under30', min: 0, max: 3000, label: 'Under ' + money(3000) },
        { key: '30-75', min: 3000, max: 7500, label: money(3000) + '–' + money(7500) },
        { key: '75-150', min: 7500, max: 15000, label: money(7500) + '–' + money(15000) },
        { key: '150plus', min: 15000, max: null, label: money(15000) + '+' }
      ];
    }

    var draftFilterState = null;
    function applyDraftFilters() {
      if(draftFilterState)Object.keys(draftFilterState).forEach(function(key){state[key]=new Set(draftFilterState[key]);});else readFilterControls();
      render();closeDrawer();scrollToCollection();
    }
    function upgradeFilterDrawer() {
      filterDrawer.addEventListener('keydown',function(event){
        if(event.key!=='Tab')return;
        var focusable=Array.from(filterDrawer.querySelectorAll('button:not(:disabled),summary,input:not(:disabled)')).filter(function(node){return node.getClientRects().length;});
        var first=focusable[0],last=focusable[focusable.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
      });
      filterDrawer.querySelector('[data-sh-apply-filters]').addEventListener('click',function(event){event.stopPropagation();applyDraftFilters();});
      filterDrawer.querySelector('[data-sh-close-filters]').addEventListener('click',function(event){event.stopPropagation();closeDrawer();});
      var body=filterDrawer.querySelector('.sh-lec__drawer-body');
      var selected=document.createElement('div');selected.className='sh-lec__drawer-selection';
      selected.innerHTML='<div><span data-sh-draft-label></span><button type="button" data-sh-draft-clear>Clear all</button></div><div data-sh-draft-chips></div><p data-sh-draft-empty hidden>No matches. Remove a filter or clear all to explore again.</p>';
      body.prepend(selected);
      ['types','prices','powers','functions'].forEach(function(key,index){
        var options=body.querySelector('[data-sh-filter-options="'+key+'"]');if(!options)return;
        var fieldset=options.closest('fieldset');if(!fieldset)return;
        var details=document.createElement('details');details.className='sh-lec__filter-group';details.open=index<2;
        var summary=document.createElement('summary');var title=document.createElement('strong');title.textContent=fieldset.querySelector('legend').textContent;
        var wrap=document.createElement('span');wrap.append(title);var subtitle=document.createElement('small');subtitle.dataset.shDraftSummary=key;wrap.append(subtitle);summary.append(wrap);var icon=document.createElement('i');icon.setAttribute('aria-hidden','true');summary.append(icon);details.append(summary,options);fieldset.remove();body.append(details);
      });
      var clear=filterDrawer.querySelector('[data-sh-reset-filters]');if(clear)clear.hidden=true;
      filterDrawer.addEventListener('change',function(event){if(event.target.matches('[data-sh-filter-input]'))updateDraftFilters();});
      filterDrawer.addEventListener('click',function(event){
        var button=event.target.closest('[data-sh-draft-clear],[data-sh-draft-remove]');if(!button)return;
        filterDrawer.querySelectorAll('[data-sh-filter-input]').forEach(function(input){if(button.hasAttribute('data-sh-draft-clear')||(input.dataset.shFilterInput===button.dataset.shDraftRemove&&input.value===button.dataset.value))input.checked=false;});updateDraftFilters();
      });
    }

    function updateDraftFilters() {
      var saved={};['types','functions','powers','prices'].forEach(function(key){saved[key]=state[key];});
      readFilterControls();
      var total=products.filter(productMatches).length,selected=0;
      var chips=filterDrawer.querySelector('[data-sh-draft-chips]');chips.replaceChildren();
      Object.keys(saved).forEach(function(key){
        var labels=[];state[key].forEach(function(value){selected++;var entry=filterDefinitions[key].find(function(option){return option[0]===value;});var label=entry?entry[1]:value;labels.push(label);var button=document.createElement('button');button.type='button';button.dataset.shDraftRemove=key;button.dataset.value=value;button.textContent=label+' ×';button.setAttribute('aria-label','Remove '+label);chips.append(button);});
        var summary=filterDrawer.querySelector('[data-sh-draft-summary="'+key+'"]');if(summary)summary.textContent=labels.join(', ');
      });
      filterDrawer.querySelector('[data-sh-draft-label]').textContent=selected?selected+' filter'+(selected===1?'':'s')+' selected':'All products';
      filterDrawer.querySelector('[data-sh-draft-clear]').disabled=!selected;
      filterDrawer.querySelector('[data-sh-draft-empty]').hidden=total>0;
      var apply=filterDrawer.querySelector('[data-sh-apply-filters]');apply.textContent=total?'Show '+total+' product'+(total===1?'':'s'):'No matching products';apply.disabled=!total;
      draftFilterState={};Object.keys(saved).forEach(function(key){draftFilterState[key]=new Set(state[key]);state[key]=saved[key];});
    }

    function populateFilters() {
      Object.keys(filterDefinitions).forEach(function (groupName) {
        var container = root.querySelector('[data-sh-filter-options="' + groupName + '"]');
        if (!container) return;
        container.innerHTML = filterDefinitions[groupName].map(function (entry) {
          var count = optionProductCount(groupName, entry[0]);
          if (!count && !state[groupName].has(entry[0])) return ''; 
          return '<label class="sh-lec__check">' +
            '<input type="checkbox" value="' + escapeHtml(entry[0]) + '" data-sh-filter-input="' + groupName + '">' +
            '<span>' + escapeHtml(entry[1]) + '</span><small>' + count + '</small></label>';
        }).join('');
      });
    }

    function optionProductCount(groupName, value) {
      return products.filter(function (product) {
        if (groupName === 'types') return product.type === value;
        if (groupName === 'functions') return product.functions.indexOf(value) !== -1;
        if (groupName === 'powers') return product.powers.indexOf(value) !== -1;
        if (groupName === 'prices') return priceMatches(product.primaryPrice, value);
        return false;
      }).length;
    }

    function updateCategoryCounts() {
      root.querySelectorAll('[data-sh-category-count-for]').forEach(function (node) {
        var view = node.dataset.shCategoryCountFor || 'all';
        node.textContent = products.filter(function (product) { return viewMatches(product, view); }).length;
      });
    }

    function syncFilterControls() {
      root.querySelectorAll('[data-sh-filter-input]').forEach(function (input) {
        var groupName = input.dataset.shFilterInput;
        input.checked = state[groupName].has(input.value);
      });
    }

    function syncViewButtons() {
      root.querySelectorAll('[data-sh-view]').forEach(function (button) {
        button.classList.toggle('is-active', button.dataset.shView === state.view);
      });
    }


    // Preserve the server-rendered cards when hydration has no results to change.
    function hasSameRenderedProducts(entries) {
      var cards = Array.from(grid.children).filter(function(node) {
        return node.matches('[data-sh-uc-card], [data-sh-card]');
      });
      return cards.length === entries.length && cards.every(function(card, index) {
        return (card.dataset.productHandle || card.dataset.shCard || card.dataset.handle) === entries[index].selected.handle;
      });
    }
    function render(updateUrl) {
      if (updateUrl !== false) writeStateToUrl();
      var visible = groups.map(function (group) {
        var matching = group.members.filter(productMatches);
        if (!matching.length) return null;
        var selectedHandle = selectedByGroup[group.key];
        var selected = matching.find(function (product) { return product.handle === selectedHandle; }) || matching[0];
        selectedByGroup[group.key] = selected.handle;
        return { group: group, matching: matching, selected: selected };
      }).filter(Boolean);

      visible.sort(sortGroupEntries);
      var hasFilters = activeFilterCount() > 0 || state.view !== 'all';
      var cards = [];
      visible.forEach(function (entry, index) {
        cards.push(cardMarkup(entry, index));
        if (!hasFilters && index === 11 && editorialTemplate) {
          cards.push(editorialTemplate.innerHTML.trim());
        }
      });

      if (!cards.length) {
        cards.push('<div class="sh-lec__empty"><h3>No lights in this edit.</h3><p>Clear a filter and the room will brighten up.</p><button type="button" data-sh-clear-all>Clear all filters</button></div>');
      }
      if (!hasSameRenderedProducts(visible)) grid.innerHTML = cards.join('');

      var productTotal = visible.reduce(function (sum, entry) { return sum + entry.matching.length; }, 0);
      resultsCount.innerHTML = '<span class="sh-lec__design-count">' + visible.length + ' design' + (visible.length === 1 ? '' : 's') + ' · </span>' + productTotal + ' product' + (productTotal === 1 ? '' : 's');
      renderApplied();
      animateCards();
    }

    function productMatches(product) {
      if (!viewMatches(product, state.view)) return false;
      if (state.types.size && !state.types.has(product.type)) return false;
      if (state.functions.size && !intersects(product.functions, state.functions)) return false;
      if (state.powers.size && !intersects(product.powers, state.powers)) return false;
      if (state.prices.size) {
        var priceOkay = Array.from(state.prices).some(function (range) { return priceMatches(product.primaryPrice, range); });
        if (!priceOkay) return false;
      }
      return true;
    }

    function viewMatches(product, view) {
      if (view === 'all') return true;
      if (view === 'table') return product.type === 'Table Lamp';
      if (view === 'floor') return product.type === 'Floor Lamp';
      if (view === 'portable') return product.powers.indexOf('Rechargeable') !== -1 || product.type === 'Night Light' || product.type === 'Motion Light';
      if (view === 'colour') return product.functions.indexOf('RGB / Colour-changing') !== -1 || product.functions.indexOf('Projection') !== -1;
      if (view === 'installed') return product.type === 'Pendant Light' || product.type === 'Wall Light';
      return true;
    }

    function intersects(values, selected) {
      return values.some(function (value) { return selected.has(value); });
    }

    function priceMatches(price, rangeKey) {
      var amount = Number(price || 0);
      var range = priceRanges.find(function (entry) { return entry.key === rangeKey; });
      if (!range) return true;
      if (amount < range.min) return false;
      return range.max === null ? true : amount < range.max;
    }

    function sortGroupEntries(a, b) {
      if (state.sort === 'price-asc') return minimumMatchingPrice(a) - minimumMatchingPrice(b);
      if (state.sort === 'price-desc') return minimumMatchingPrice(b) - minimumMatchingPrice(a);
      if (state.sort === 'rating') return maximumRating(b) - maximumRating(a) || a.group.order - b.group.order;
      if (state.sort === 'featured') return a.group.order - b.group.order;
      return a.group.order - b.group.order;
    }

    function minimumMatchingPrice(entry) {
      return Math.min.apply(null, entry.matching.map(function (product) { return product.primaryPrice; }));
    }

    function maximumRating(entry) {
      return Math.max.apply(null, entry.matching.map(function (product) { return product.rating || 0; }));
    }


    function compactCardMeta(product) {
      var tags = arrayValue(product && product.tags).map(function (tag) { return String(tag || '').toLowerCase(); });
      var lightingTag = tags.find(function (tag) { return tag.indexOf('lighting:type:') === 0; });
      if (lightingTag) {
        var lt = lightingTag.replace('lighting:type:', '');
        var lm = {'table-lamp':'Table lamp','floor-lamp':'Floor lamp','wall-light':'Wall light','pendant-light':'Pendant light','projection-light':'Projection light','night-light':'Night light','motion-light':'Motion light'};
        if (lm[lt]) return lm[lt];
      }
      var artTag = tags.find(function (tag) { return tag.indexOf('art-orientation:') === 0; });
      if (artTag) return artTag.indexOf('landscape') !== -1 ? 'Landscape print' : (artTag.indexOf('portrait') !== -1 ? 'Portrait print' : 'Art print');
      if (/diffuser/i.test(String(product.type || '')+' '+String(product.title || ''))) return 'Diffuser';
      var accessoryTag = tags.find(function (tag) { return tag.indexOf('accessory:category:') === 0; });
      if (accessoryTag) {
        var ac = accessoryTag.replace('accessory:category:', '');
        var am = {'decorative-objects':'Decor object','wall-and-display':'Wall & display','vases-and-planters':'Vase / planter','tabletop':'Tabletop object','storage-and-organisation':'Storage','soft-furnishings':'Soft furnishing','furniture-accents':'Furniture accent','entryway':'Entryway','small-accessories':'Small accessory','fragrance-and-candlelight':'Candlelight'};
        if (am[ac]) return am[ac];
      }
      var type = String(product && product.type || '').trim();
      var category = String(product && product.category || '').trim();
      var tm = {'Art Prints':'Art print','Poster':'Art print','Wall Tapestries':'Wall tapestry','Table Lamp':'Table lamp','Floor Lamp':'Floor lamp','Wall Light':'Wall light','Pendant Light':'Pendant light','Projection Light':'Projection light','Night Light':'Night light','Motion Light':'Motion light','Decorative Sculpture':'Decor object','Decorative Mirror':'Mirror','Decorative Tray':'Tray','Ashtray':'Ashtray','Aroma Diffuser':'Aroma diffuser','Candle Holder':'Candle holder','Hanging Planter':'Planter','Vase':'Vase','Photo Frame':'Photo frame','Wall Clock':'Clock','Table Clock':'Clock','Wall Calendar':'Calendar','Chairs & Stools':'Chair','Chair':'Chair','Lounge Chair':'Chair','Footstool':'Footstool','Tables':'Table','Bookshelf':'Storage','Shelving':'Storage','Wall Shelf':'Storage','Desktop Storage':'Storage','Waste Bin':'Storage','Bedding':'Bedding','Blankets':'Throw / blanket','Rugs':'Rug','Door Mat':'Door mat','Shower Curtain':'Shower curtain','Cushion Cover':'Cushion cover','Indoor Slippers':'Slippers','Fridge Magnets':'Magnets','Home Accents':'Home accent','Gift Cards':'Gift card','Projectors':'Projector'};
      if (tm[type]) return tm[type];
      var cm = {'Decorative Objects':'Decor object','Wall & Display':'Wall & display','Vases & Planters':'Vase / planter','Tabletop':'Tabletop object','Storage & Organisation':'Storage','Soft Furnishings':'Soft furnishing','Furniture Accents':'Furniture accent','Rugs & mats':'Rug / mat','Throws & blankets':'Throw / blanket','Bath textiles':'Bath textile','Cushions':'Cushion'};
      if (cm[category]) return cm[category];
      var title = String(product && product.title || '').toLowerCase();
      if (!category && !type) {
        if (title.indexOf('shower curtain') !== -1) return 'Shower curtain';
        if (title.indexOf('lamp') !== -1 || title.indexOf('light') !== -1) return 'Lighting';
        if (title.indexOf('print') !== -1 || title.indexOf('poster') !== -1) return 'Art print';
        if (title.indexOf('rug') !== -1) return 'Rug';
        if (title.indexOf('mat') !== -1) return 'Mat';
        if (title.indexOf('chair') !== -1 || title.indexOf('lounger') !== -1) return 'Chair';
        if (title.indexOf('table') !== -1) return 'Table';
        if (title.indexOf('vase') !== -1) return 'Vase';
        if (title.indexOf('clock') !== -1 || title.indexOf('calendar') !== -1) return 'Clock / calendar';
        if (title.indexOf('shelf') !== -1 || title.indexOf('storage') !== -1) return 'Storage';
        if (title.indexOf('slipper') !== -1) return 'Slippers';
        if (title.indexOf('tapestry') !== -1 || title.indexOf('throw') !== -1) return 'Textile';
        if (title.indexOf('frame') !== -1) return 'Frame';
        if (title.indexOf('mirror') !== -1) return 'Mirror';
      }
      var fallback = category || type || 'SickHaus object';
      return fallback.length > 22 ? fallback.split(/\s+/).slice(0, 2).join(' ') : fallback;
    }

    function cardMarkup(entry, index) {
      var group = entry.group;
      var product = entry.selected;
      var members = entry.matching;
      var selectedIndex = members.findIndex(function (member) { return member.handle === product.handle; });
      var groupControls = members.length > 1 ? memberSwatches(members, selectedIndex, group.key) : productSwatches(product);
      var available = product.available;
      var fact = product.fact || '';
      var classification = product.classification || product.type || 'Lighting';
      var label = product.designLabel || (members.length > 1 ? product.title : product.family || product.type);
      var image = product.image || '';
      var secondary = product.secondaryImage || image;
      var quick = available
        ? (quickAddEnabled ? '<button class="sh-lec__quick-button" type="button" data-sh-quick="' + escapeHtml(product.handle) + '">Quick add</button>' : '')
        : '<span class="sh-lec__sold">Currently unavailable</span>';
      var familyNote = members.length > 1 ? '<span class="sh-lec__family-note" data-sh-family-note>' + members.length + ' designs · tap a colour</span>' : '';
      var rating = '<span class="sh-lec__rating" data-sh-card-rating' + (product.rating > 0 ? '' : ' hidden') + '>' +
        (product.rating > 0 ? '<strong>★ ' + product.rating.toFixed(1) + '</strong>' + (product.reviewCount ? ' (' + product.reviewCount + ')' : '') : '') +
        '</span>';
      var savedClass = saved.has(group.key) ? ' is-saved' : '';
      return '<article class="sh-lec__card" data-sh-card data-group="' + escapeHtml(group.key) + '" data-handle="' + escapeHtml(product.handle) + '" style="--sh-delay:' + Math.min(index, 11) * 35 + 'ms">' +
        '<div class="sh-lec__media">' +
          '<a href="' + escapeHtml(product.url) + '" aria-label="View ' + escapeHtml(product.title) + '" data-sh-product-link>' +
            '<img ' + window.SHResponsiveCards.attributes(image) + ' alt="' + escapeHtml(product.imageAlt || product.title) + '" loading="lazy" data-sh-primary-image>' +
            '<img ' + window.SHResponsiveCards.attributes(secondary, true, image) + ' alt="" loading="lazy" aria-hidden="true" data-sh-secondary-image>' +
          '</a>' +
          (group.badge ? '<span class="sh-lec__badge" data-sh-card-badge>' + escapeHtml(group.badge) + '</span>' : '') +
          '<button class="sh-lec__save' + savedClass + '" type="button" data-sh-save="' + escapeHtml(group.key) + '" aria-label="Save ' + escapeHtml(group.title) + '" aria-pressed="' + (saved.has(group.key) ? 'true' : 'false') + '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.4 4.5 13.2A4.9 4.9 0 0 1 11.4 6l.6.7.6-.7a4.9 4.9 0 0 1 6.9 7.2Z"/></svg></button>' +
          familyNote + quick +
        '</div>' +
        '<div class="sh-lec__info">' +
          '<div class="sh-lec__kicker"><span data-sh-card-classification>' + escapeHtml(classification) + '</span><span data-sh-card-label>' + escapeHtml(label) + '</span></div>' +
          '<div class="sh-lec__title-row"><h3><a href="' + escapeHtml(product.url) + '" data-sh-card-title data-sh-product-link>' + escapeHtml(group.title) + '</a></h3><div class="sh-lec__price" data-sh-card-price>' + priceMarkup(product) + '</div></div>' +
          '<p class="sh-lec__fact" data-sh-card-fact>' + escapeHtml(fact) + '</p>' +
          '<div class="sh-lec__card-footer"><div class="sh-lec__swatches" data-sh-card-swatches>' + groupControls + '</div>' + rating + '</div>' +
        '</div>' +
      '</article>';
    }

    function memberSwatches(members, selectedIndex, groupKey) {
      var visible = members;
      var markup = visible.map(function (member, index) {
        var label = member.designLabel || member.title;
        var colour = colourFor(member.colours[0] || member.designLabel || member.title);
        var available = member.available !== false;
        return '<button type="button" class="sh-lec__swatch' + (index === selectedIndex ? ' is-active' : '') + (!colour ? ' is-text' : '') + (!available ? ' is-unavailable' : '') + '"' + (colour ? ' style="--sh-swatch:' + colour + ';background:' + colour + '" data-sh-swatch-kind="colour"' : ' data-sh-swatch-kind="text"') + ' data-sh-member="' + escapeHtml(member.handle) + '" data-sh-group="' + escapeHtml(groupKey) + '" aria-label="Choose ' + escapeHtml(label) + '" title="' + escapeHtml(label) + '"' + (available ? '' : ' disabled aria-disabled="true" data-available="false"') + '>' + (colour ? '' : escapeHtml(shortSwatchLabel(label))) + '</button>';
      }).join('');
      if (members.length > visible.length) markup += '<span class="sh-lec__swatch-count">+' + (members.length - visible.length) + '</span>';
      return markup;
    }

    function productSwatches(product) {
      var choices = colourVariantChoices(product);
      if (!choices.length) return '';
      var preferred = preferredVariantByProduct[product.handle];
      return choices.map(function (choice, index) {
        var active = preferred ? String(choice.variantId) === String(preferred) : index === 0;
        var colour = colourFor(choice.label);
        return '<button type="button" class="sh-lec__swatch' + (active ? ' is-active' : '') + (!colour ? ' is-text' : '') + (!choice.available ? ' is-unavailable' : '') + '"' + (colour ? ' style="--sh-swatch:' + colour + ';background:' + colour + '" data-sh-swatch-kind="colour"' : ' data-sh-swatch-kind="text"') + ' data-sh-product-swatch="' + escapeHtml(product.handle) + '"' + (choice.variantId ? ' data-variant-id="' + choice.variantId + '"' : '') + ' aria-label="Choose ' + escapeHtml(choice.label) + '" title="' + escapeHtml(choice.label) + '"' + (choice.available ? '' : ' disabled aria-disabled="true" data-available="false"') + '>' + (colour ? '' : escapeHtml(shortSwatchLabel(choice.label))) + '</button>';
      }).join('');
    }

    function isColourOptionName(name) {
      var normalized = String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
      return normalized.indexOf('colour') !== -1 ||
        normalized.indexOf('color') !== -1 ||
        normalized.indexOf('finish') !== -1 ||
        normalized.indexOf('upholstery') !== -1 ||
        normalized.indexOf('fabric') !== -1 ||
        normalized === 'material' ||
        normalized === 'colourway';
    }

    function colourVariantChoices(product) {
      var byLabel = new Map();
      product.variants.forEach(function (variant) {
        var option = variant.options.find(function (entry) { return isColourOptionName(entry.name); });
        if (!option) return;
        var label = String(option.value || '').trim();
        var key = label.toLowerCase();
        if (!label) return;
        var current = byLabel.get(key);
        if (!current || (!current.available && variant.available)) {
          byLabel.set(key, { label: label, variantId: variant.id, available: !!variant.available });
        }
      });
      return Array.from(byLabel.values());
    }

    function priceMarkup(product, variant) {
      var displayVariant = variant || product.availableVariants.find(function (item) { return item.price === product.primaryPrice; }) || null;
      var price = displayVariant ? displayVariant.price : product.price;
      var compareAt = displayVariant ? displayVariant.compareAt : product.compareAt;
      var formattedPrice = displayVariant && displayVariant.priceFormatted ? displayVariant.priceFormatted : (product.priceFormatted || money(price));
      var formattedCompareAt = displayVariant && displayVariant.compareAtFormatted ? displayVariant.compareAtFormatted : (product.compareAtFormatted || money(compareAt));
      var from = !variant && product.availableVariants.some(function (item) { return item.price !== product.primaryPrice; }) ? '<small>From </small>' : '';
      return (compareAt > price ? '<s>' + formattedCompareAt + '</s>' : '') + '<span>' + from + formattedPrice + '</span>';
    }

    function money(cents) {
      var amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
      } catch (error) {
        try {
          return currency + ' ' + amount.toLocaleString(locale, { minimumFractionDigits: amount % 1 ? 2 : 0, maximumFractionDigits: 2 });
        } catch (nestedError) {
          return currency + ' ' + amount.toFixed(amount % 1 ? 2 : 0);
        }
      }
    }

    function colourFor(value) {
      var key = String(value || '').toLowerCase();
      if (key.indexOf('multicolour') !== -1 || key.indexOf('colourful') !== -1) return 'linear-gradient(135deg,#ff5b2f,#ef75c6 50%,#6578ff)';
      var map = {
        orange:'#e76b2c', yellow:'#e8bd23', red:'#a91d24', green:'#718a67', 'dark green':'#31493a', blue:'#4d6d93', pink:'#dda2b2',
        black:'#1c1c1c', white:'#f1eee6', cream:'#dfd2b9', grey:'#9b9d9b', gray:'#9b9d9b', brown:'#8b684d', silver:'#b9babc',
        gold:'#c7a451', bronze:'#9b6845', amber:'#ba6a2a', clear:'linear-gradient(135deg,#fff,rgba(255,255,255,.2))', smoked:'#686765'
      };
      var found = Object.keys(map).find(function (name) { return key.indexOf(name) !== -1; });
      return found ? map[found] : '';
    }


    function shortSwatchLabel(value) {
      var text = String(value || '').trim();
      if (text.length <= 9) return text;
      var words = text.split(/\s*\/\s*|\s+-\s+|\s+/).filter(Boolean);
      if (words.length > 1) return words.map(function (word) { return word.charAt(0); }).join('').slice(0, 5).toUpperCase();
      return text.slice(0, 8) + '…';
    }

    function renderApplied() {
      var labels = Object.create(null);
      Object.keys(filterDefinitions).forEach(function (group) {
        filterDefinitions[group].forEach(function (entry) { labels[entry[0]] = entry[1]; });
      });
      var html = '';
      [['types', state.types], ['functions', state.functions], ['powers', state.powers], ['prices', state.prices]].forEach(function (entry) {
        entry[1].forEach(function (value) {
          html += '<span class="sh-lec__chip">' + escapeHtml(labels[value] || value) + '<button type="button" data-sh-remove-filter="' + entry[0] + '" data-value="' + escapeHtml(value) + '" aria-label="Remove ' + escapeHtml(labels[value] || value) + '">×</button></span>';
        });
      });
      if (state.view !== 'all') html += '<span class="sh-lec__chip">' + escapeHtml(viewLabel(state.view)) + '<button type="button" data-sh-view="all" aria-label="Show all lighting">×</button></span>';
      if (activeFilterCount() || state.view !== 'all') html += '<button class="sh-lec__clear-all" type="button" data-sh-clear-all>Clear all</button>';
      applied.innerHTML = html;
      var count = activeFilterCount();
      filterCount.hidden = !count;
      filterCount.textContent = count;
    }

    function activeFilterCount() {
      return state.types.size + state.functions.size + state.powers.size + state.prices.size;
    }

    function viewLabel(view) {
      return { table:'Table lamps', floor:'Floor lamps', portable:'Portable', colour:'Colour & projection', installed:'Wall & ceiling' }[view] || 'All lighting';
    }

    function animateCards() {
      var cards = grid.querySelectorAll('[data-sh-card]');
      if (!('IntersectionObserver' in window)) {
        cards.forEach(function (card) { card.classList.add('is-visible'); });
        return;
      }
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.style.animationDelay = entry.target.style.getPropertyValue('--sh-delay') || '0ms';
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: .06, rootMargin: '80px 0px' });
      cards.forEach(function (card) { observer.observe(card); });
    }

    function onClick(event) {
      var button = event.target.closest('button');
      if (!button || !root.contains(button)) return;

      if (button.matches('[data-sh-open-filters]')) return openDrawer(button);
      if (button.matches('[data-sh-close-filters]')) return closeDrawer();
      if (button.matches('[data-sh-apply-filters]')) { applyDraftFilters(); return; }
      if (button.matches('[data-sh-reset-filters]')) { clearFilters(); return; }
      if (button.matches('[data-sh-close-quick]')) return closeQuick();
      if (button.matches('[data-sh-modal-add]')) return addActiveVariant(button);
      if (button.matches('[data-sh-clear-all]')) { clearFilters(); return; }

      if (button.dataset.shView) {
        state.view = button.dataset.shView;
        clearFilterSets();
        syncViewButtons();
        syncFilterControls();
        render();
        scrollToCollection();
        return;
      }
      if (button.dataset.shMember) {
        switchGroupMember(button);
        return;
      }
      if (button.dataset.shProductSwatch) {
        switchProductVariant(button);
        return;
      }
      if (button.dataset.shQuick) {
        openQuick(button.dataset.shQuick, button);
        return;
      }
      if (button.dataset.shSave) {
        toggleSaved(button.dataset.shSave, button);
        return;
      }
      if (button.dataset.shRemoveFilter) {
        state[button.dataset.shRemoveFilter].delete(button.dataset.value);
        syncFilterControls();
        render();
        return;
      }
      if (button.dataset.shEditorialFunction) {
        state.view = 'all'; clearFilterSets(); state.functions.add(button.dataset.shEditorialFunction); syncViewButtons(); syncFilterControls(); render(); scrollToFirstProductCard();
        return;
      }
      if (button.dataset.shGuideFunction) {
        state.view = 'all'; clearFilterSets(); state.functions.add(button.dataset.shGuideFunction); syncViewButtons(); syncFilterControls(); render(); scrollToFirstProductCard();
        return;
      }
      if (button.dataset.shGuidePower) {
        state.view = 'all'; clearFilterSets(); state.powers.add(button.dataset.shGuidePower); syncViewButtons(); syncFilterControls(); render(); scrollToFirstProductCard();
      }
    }

    function onPointerIntent(event) {
      var target = event.target.closest ? event.target.closest('[data-sh-member],[data-sh-product-swatch]') : null;
      if (!target || !root.contains(target)) return;
      if (target.dataset.shMember) {
        var member = products.find(function (product) { return product.handle === target.dataset.shMember; });
        if (member) preloadProductAssets(member);
        return;
      }
      if (target.dataset.variantId) {
        var product = products.find(function (item) { return item.handle === target.dataset.shProductSwatch; });
        var variant = product && product.variants.find(function (item) { return String(item.id) === String(target.dataset.variantId); });
        if (variant && variant.image) preloadImage(variant.image);
      }
    }

    function switchGroupMember(button) {
      var groupKey = button.dataset.shGroup;
      var handle = button.dataset.shMember;
      var group = groups.find(function (item) { return item.key === groupKey; });
      var card = button.closest('[data-sh-card]');
      if (!group || !card || !canGroupFamily(groupKey)) return;
      var matching = group.members.filter(productMatches);
      var selected = matching.find(function (product) { return product.handle === handle; });
      if (!selected || selected.handle === card.dataset.handle) return;

      selectedByGroup[groupKey] = selected.handle;
      var token = String(++switchSequence);
      card.dataset.shSwitchToken = token;
      button.setAttribute('aria-busy', 'true');

      preloadProductAssets(selected).then(function () {
        if (!card.isConnected || card.dataset.shSwitchToken !== token) return;
        patchCard(card, { group: group, matching: matching, selected: selected });
      }).finally(function () {
        if (button.isConnected) button.removeAttribute('aria-busy');
      });
    }

    function switchProductVariant(button) {
      var handle = button.dataset.shProductSwatch;
      var product = products.find(function (item) { return item.handle === handle; });
      var card = button.closest('[data-sh-card]');
      if (!product || !card) return;
      var variant = product.variants.find(function (item) { return String(item.id) === String(button.dataset.variantId); });
      if (!variant) return;

      preferredVariantByProduct[handle] = variant.id;
      card.querySelectorAll('[data-sh-product-swatch]').forEach(function (swatch) { swatch.classList.remove('is-active'); });
      button.classList.add('is-active');

      var selectedOption = variant.options.find(function (option) { return isColourOptionName(option.name); });
      var labelNode = card.querySelector('[data-sh-card-label]');
      if (labelNode && selectedOption) labelNode.textContent = selectedOption.value;
      var priceNode = card.querySelector('[data-sh-card-price]');
      if (priceNode) priceNode.innerHTML = priceMarkup(product, variant);
      card.querySelectorAll('[data-sh-product-link]').forEach(function (link) {
        link.href = product.url + '?variant=' + encodeURIComponent(variant.id);
      });

      if (!variant.image) return;
      var token = String(++switchSequence);
      card.dataset.shSwitchToken = token;
      preloadImage(variant.image).then(function () {
        if (!card.isConnected || card.dataset.shSwitchToken !== token) return;
        crossfadeCardImage(card, variant.image, product.imageAlt || product.title, variant.image);
      });
    }

    function patchCard(card, entry) {
      var group = entry.group;
      var product = entry.selected;
      var members = entry.matching;
      var selectedIndex = members.findIndex(function (member) { return member.handle === product.handle; });
      var groupControls = members.length > 1 ? memberSwatches(members, selectedIndex, group.key) : productSwatches(product);
      var classification = product.classification || product.type || 'Lighting';
      var label = product.designLabel || (members.length > 1 ? product.title : product.family || product.type);
      var image = product.image || '';
      var secondary = product.secondaryImage || image;

      card.dataset.handle = product.handle;
      card.querySelectorAll('[data-sh-product-link]').forEach(function (link) {
        link.href = product.url;
        if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', 'View ' + product.title);
      });
      crossfadeCardImage(card, image, product.imageAlt || product.title, secondary);

      setText(card, '[data-sh-card-classification]', classification);
      setText(card, '[data-sh-card-label]', label);
      setText(card, '[data-sh-card-title]', group.title);
      setText(card, '[data-sh-card-fact]', product.fact || '');
      var priceNode = card.querySelector('[data-sh-card-price]');
      if (priceNode) priceNode.innerHTML = priceMarkup(product);
      var swatches = card.querySelector('[data-sh-card-swatches]');
      if (swatches) swatches.innerHTML = groupControls;
      updateCardRating(card, product);
      updateCardBadge(card, group.badge || product.badge || '');
      updateCardAction(card, product);
      var familyNote = card.querySelector('[data-sh-family-note]');
      if (familyNote) familyNote.textContent = members.length + ' designs · tap a colour';
      var saveButton = card.querySelector('[data-sh-save]');
      if (saveButton) saveButton.setAttribute('aria-label', 'Save ' + group.title);
      card.classList.add('is-product-updated');
      window.setTimeout(function () { card.classList.remove('is-product-updated'); }, 420);
    }

    function setText(scope, selector, value) {
      var node = scope.querySelector(selector);
      if (node) node.textContent = value || '';
    }

    function updateCardRating(card, product) {
      var node = card.querySelector('[data-sh-card-rating]');
      if (!node) return;
      if (product.rating > 0) {
        node.hidden = false;
        node.innerHTML = '<strong>★ ' + product.rating.toFixed(1) + '</strong>' + (product.reviewCount ? ' (' + product.reviewCount + ')' : '');
      } else {
        node.hidden = true;
        node.textContent = '';
      }
    }

    function updateCardBadge(card, value) {
      var media = card.querySelector('.sh-lec__media');
      var badge = card.querySelector('[data-sh-card-badge]');
      if (!value) {
        if (badge) badge.remove();
        return;
      }
      if (!badge && media) {
        badge = document.createElement('span');
        badge.className = 'sh-lec__badge';
        badge.setAttribute('data-sh-card-badge', '');
        media.appendChild(badge);
      }
      if (badge) badge.textContent = value;
    }

    function updateCardAction(card, product) {
      var media = card.querySelector('.sh-lec__media');
      var quick = card.querySelector('[data-sh-quick]');
      var sold = card.querySelector('.sh-lec__sold');
      if (product.available) {
        if (sold) sold.remove();
        if (!quickAddEnabled) {
          if (quick) quick.remove();
          return;
        }
        if (!quick && media) {
          quick = document.createElement('button');
          quick.className = 'sh-lec__quick-button';
          quick.type = 'button';
          quick.textContent = 'Quick add';
          media.appendChild(quick);
        }
        if (quick) quick.dataset.shQuick = product.handle;
      } else {
        if (quick) quick.remove();
        if (!sold && media) {
          sold = document.createElement('span');
          sold.className = 'sh-lec__sold';
          sold.textContent = 'Currently unavailable';
          media.appendChild(sold);
        }
      }
    }

    function preloadProductAssets(product) {
      return Promise.all([preloadImage(product.image), preloadImage(product.secondaryImage || product.image)]);
    }

    function preloadImage(url) {
      if (!url) return Promise.resolve();
      if (imagePromises.has(url)) return imagePromises.get(url);
      var promise = new Promise(function (resolve) {
        var image = new Image();
        image.onload = function () {
          if (typeof image.decode === 'function') image.decode().catch(function () {}).finally(resolve);
          else resolve();
        };
        image.onerror = resolve;
        image.src = url;
        if (image.complete) image.onload();
      });
      imagePromises.set(url, promise);
      return promise;
    }

    function crossfadeCardImage(card, source, alt, secondarySource) {
      var anchor = card.querySelector('.sh-lec__media > a');
      var primary = card.querySelector('[data-sh-primary-image]');
      var secondary = card.querySelector('[data-sh-secondary-image]');
      if (!anchor || !primary || !source) return;
      if (primary.src === source || primary.getAttribute('src') === source) {
        primary.alt = alt || '';
        if (secondary && secondarySource) window.SHResponsiveCards.set(secondary,secondarySource);
        return;
      }

      anchor.querySelectorAll('.sh-lec__transition-image').forEach(function (item) { item.remove(); });
      var overlay = document.createElement('img');
      overlay.className = 'sh-lec__transition-image';
      overlay.src=source;
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      anchor.appendChild(overlay);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { overlay.classList.add('is-active'); });
      });
      window.setTimeout(function () {
        window.SHResponsiveCards.set(primary,source);
        primary.alt = alt || '';
        if (secondary) window.SHResponsiveCards.set(secondary,secondarySource || source);
        overlay.remove();
      }, prefersReducedMotion() ? 20 : 300);
    }

    function onChange(event) {
      if (event.target.matches('[data-sh-sort]')) {
        state.sort = event.target.value;
        render();
        return;
      }
      if (event.target.matches('[data-sh-quick-option]')) updateModalVariant();
    }

    function readFilterControls() {
      ['types', 'functions', 'powers', 'prices'].forEach(function (groupName) {
        state[groupName] = new Set(Array.from(filterDrawer.querySelectorAll('[data-sh-filter-input="' + groupName + '"]:checked')).map(function (input) { return input.value; }));
      });
    }

    function clearFilterSets() {
      state.types.clear(); state.functions.clear(); state.powers.clear(); state.prices.clear();
    }

    function clearFilters() {
      state.view = 'all'; clearFilterSets(); state.sort = 'featured';
      if (sortSelect) sortSelect.value = 'featured';
      syncViewButtons(); syncFilterControls(); render();
    }

    function openDrawer(trigger) {
      lastFocus = trigger || document.activeElement;
      syncFilterControls();
      updateDraftFilters();
      filterDrawer.classList.add('is-open'); filterBackdrop.classList.add('is-open');
      filterDrawer.setAttribute('aria-hidden', 'false');
      lockBody(true);
      setTimeout(function () { filterDrawer.querySelector('input,button')?.focus(); }, 120);
    }

    function closeDrawer() {
      filterDrawer.classList.remove('is-open'); filterBackdrop.classList.remove('is-open');
      filterDrawer.setAttribute('aria-hidden', 'true');
      lockBody(false);
      if (lastFocus) lastFocus.focus();
    }

    function openQuick(handle, trigger) {
      activeModalProduct = products.find(function (product) { return product.handle === handle; });
      if (!activeModalProduct) return;
      lastFocus = trigger || document.activeElement;
      var preferred = preferredVariantByProduct[handle];
      activeVariant = activeModalProduct.variants.find(function (variant) { return String(variant.id) === String(preferred); }) || activeModalProduct.availableVariants[0] || activeModalProduct.variants[0] || null;
      renderQuickModal();
      modal.classList.add('is-open'); modalBackdrop.classList.add('is-open'); modal.setAttribute('aria-hidden', 'false');
      lockBody(true);
      setTimeout(function () { modal.querySelector('[data-sh-close-quick]')?.focus(); }, 120);
    }

    function renderQuickModal() {
      if (!activeModalProduct) return;
      var image = activeVariant && activeVariant.image ? activeVariant.image : activeModalProduct.image;
      var imageNode = modal.querySelector('[data-sh-quick-image]');
      imageNode.src = image || '';
      imageNode.alt = activeModalProduct.imageAlt || activeModalProduct.title;
      modal.querySelector('[data-sh-quick-kicker]').textContent = (activeModalProduct.classification || activeModalProduct.type) + ' / ' + (activeModalProduct.family || 'Lighting');
      modal.querySelector('[data-sh-quick-title]').textContent = activeModalProduct.title;
      modal.querySelector('[data-sh-quick-fact]').textContent = activeModalProduct.fact || '';
      modal.querySelector('[data-sh-quick-link]').href = activeModalProduct.url;
      modal.querySelector('[data-sh-quick-options]').innerHTML = optionControlsMarkup(activeModalProduct, activeVariant);
      updateModalVariant();
    }

    function optionControlsMarkup(product, variant) {
      if (!product.variants.length || (product.variants.length === 1 && product.variants[0].title === 'Default Title')) return '';
      var optionMap = new Map();
      product.variants.forEach(function (item) {
        item.options.forEach(function (option) {
          if (String(option.name).toLowerCase() === 'title' && String(option.value).toLowerCase() === 'default title') return;
          if (!optionMap.has(option.name)) optionMap.set(option.name, []);
          if (optionMap.get(option.name).indexOf(option.value) === -1) optionMap.get(option.name).push(option.value);
        });
      });
      return Array.from(optionMap.entries()).map(function (entry) {
        var selected = variant && variant.options.find(function (option) { return option.name === entry[0]; });
        return '<div class="sh-lec__option"><label><span>' + escapeHtml(entry[0]) + '</span><span data-sh-option-selected="' + escapeHtml(entry[0]) + '">' + escapeHtml(selected ? selected.value : entry[1][0]) + '</span></label>' +
          '<select data-sh-quick-option="' + escapeHtml(entry[0]) + '">' + entry[1].map(function (value) {
            return '<option value="' + escapeHtml(value) + '"' + (selected && selected.value === value ? ' selected' : '') + '>' + escapeHtml(value) + '</option>';
          }).join('') + '</select></div>';
      }).join('');
    }

    function updateModalVariant() {
      if (!activeModalProduct) return;
      var selections = Object.create(null);
      modal.querySelectorAll('[data-sh-quick-option]').forEach(function (select) {
        selections[select.dataset.shQuickOption] = select.value;
        var display = modal.querySelector('[data-sh-option-selected="' + cssEscape(select.dataset.shQuickOption) + '"]');
        if (display) display.textContent = select.value;
      });
      if (Object.keys(selections).length) {
        activeVariant = activeModalProduct.variants.find(function (variant) {
          return Object.keys(selections).every(function (name) {
            return variant.options.some(function (option) { return option.name === name && option.value === selections[name]; });
          });
        }) || null;
      }
      var priceNode = modal.querySelector('[data-sh-quick-price]');
      priceNode.innerHTML = priceMarkup(activeModalProduct, activeVariant);
      var addButton = modal.querySelector('[data-sh-modal-add]');
      var status = modal.querySelector('[data-sh-quick-status]');
      var available = activeVariant && activeVariant.available;
      addButton.disabled = !available;
      addButton.textContent = available ? 'Add to bag' : 'Currently unavailable';
      status.textContent = activeVariant && !activeVariant.available ? 'This combination is currently unavailable.' : '';
      if (activeVariant && activeVariant.image) crossfadeQuickImage(activeVariant.image);
    }

    function crossfadeQuickImage(source) {
      var imageNode = modal.querySelector('[data-sh-quick-image]');
      var container = modal.querySelector('.sh-lec__quick-media');
      if (!imageNode || !container || !source || imageNode.getAttribute('src') === source || imageNode.src === source) return;
      preloadImage(source).then(function () {
        if (!modal.classList.contains('is-open') || !activeVariant || activeVariant.image !== source) return;
        container.querySelectorAll('.sh-lec__quick-transition-image').forEach(function (item) { item.remove(); });
        var overlay = document.createElement('img');
        overlay.className = 'sh-lec__quick-transition-image';
        overlay.src=source;
        overlay.alt = '';
        overlay.setAttribute('aria-hidden', 'true');
        container.appendChild(overlay);
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { overlay.classList.add('is-active'); });
        });
        window.setTimeout(function () {
          imageNode.src = source;
          overlay.remove();
        }, prefersReducedMotion() ? 20 : 280);
      });
    }

    function closeQuick() {
      modal.classList.remove('is-open'); modalBackdrop.classList.remove('is-open'); modal.setAttribute('aria-hidden', 'true');
      lockBody(false); activeModalProduct = null; activeVariant = null;
      if (lastFocus) lastFocus.focus();
    }

    function addActiveVariant(button) {
      if (!activeVariant || !activeVariant.available || button.disabled) return;
      var original = button.textContent;
      button.disabled = true; button.textContent = 'Adding…';
      fetch(root.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(activeVariant.id), quantity: 1 }] })
      }).then(function (response) {
        return response.json().then(function (payload) {
          if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this product.');
          return payload;
        });
      }).then(function (payload) {
        return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (response) { return response.json(); })
          .then(function (cart) { return { payload: payload, cart: cart }; });
      }).then(function (result) {
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result.cart } }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { source: 'lighting-editorial', product: activeModalProduct, variant: activeVariant } }));
        closeQuick();
      }).catch(function (error) {
        modal.querySelector('[data-sh-quick-status]').textContent = error.message || 'We could not add that option. Please try again.';
        button.disabled = false; button.textContent = original;
      });
    }

    function toggleSaved(key, button) {
      if (saved.has(key)) saved.delete(key); else saved.add(key);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(saved))); } catch (error) {}
      button.classList.toggle('is-saved', saved.has(key));
      button.setAttribute('aria-pressed', saved.has(key) ? 'true' : 'false');
    }

    function readSaved() {
      try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); } catch (error) { return new Set(); }
    }

    function showToast(message) {
      toast.textContent = message;
      toast.classList.add('is-showing');
      clearTimeout(showToast.timer);
      showToast.timer = setTimeout(function () { toast.classList.remove('is-showing'); }, 2300);
    }

    function scrollToCollection() {
      root.querySelector('.sh-lec__toolbar-shell')?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    function scrollToFirstProductCard() {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          var firstCard = grid.querySelector('[data-sh-card]');
          if (!firstCard) {
            scrollToCollection();
            return;
          }
          var toolbar = root.querySelector('.sh-lec__toolbar-shell');
          var toolbarStyle = toolbar ? window.getComputedStyle(toolbar) : null;
          var stickyTop = toolbarStyle ? parseFloat(toolbarStyle.top) || 0 : 0;
          var offset = (toolbar ? toolbar.offsetHeight : 0) + stickyTop + 14;
          var targetTop = window.scrollY + firstCard.getBoundingClientRect().top - offset;
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        });
      });
    }

    function lockBody(locked) {
      document.documentElement.classList.toggle('sh-lec-modal-open', locked);
      document.body.style.overflow = locked ? 'hidden' : '';
    }

    function onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (modal.classList.contains('is-open')) closeQuick();
      else if (filterDrawer.classList.contains('is-open')) closeDrawer();
    }

    filterBackdrop.addEventListener('click', closeDrawer);
    modalBackdrop.addEventListener('click', closeQuick);

    function prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value).replace(/[&<>'"]/g, function (character) {
        return { '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[character];
      });
    }

    function cssEscape(value) {
      if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
      return String(value).replace(/(["\\])/g, '\\$1');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
}());




